import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { adminJson } from '../utils/adminAuth';

export default function AdminPhotographers() {
  const navigate = useNavigate();
  const [photographers, setPhotographers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [selected, setSelected] = useState(null);     // photographer object
  const [assigned, setAssigned] = useState(new Set()); // dealership ids (numbers)
  const [dealerSearch, setDealerSearch] = useState('');
  const [photogSearch, setPhotogSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('adminAuth');
    if (!token) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const loadPhotographers = useCallback(() => {
    const h = authHeader();
    if (!h) return;
    fetch(`${API_BASE}/photographer/photographers`, { headers: h })
      .then(adminJson)
      .then(d => setPhotographers(Array.isArray(d) ? d : []))
      .catch(() => setError('Failed to load photographers'));
  }, [authHeader]);

  useEffect(() => {
    const h = authHeader();
    if (!h) return;
    loadPhotographers();
    fetch(`${API_BASE}/admin/dealers?limit=500`, { headers: h })
      .then(adminJson)
      .then(d => setDealers((Array.isArray(d) ? d : (d.dealers || d.dealerships || []))))
      .catch(() => setError('Failed to load dealers'));
  }, [authHeader, loadPhotographers]);

  const selectPhotog = (p) => {
    const h = authHeader();
    if (!h) return;
    setSelected(p);
    setMessage('');
    setLoading(true);
    fetch(`${API_BASE}/photographer/photographers/${p.id}/assignments`, { headers: h })
      .then(adminJson)
      .then(d => setAssigned(new Set((Array.isArray(d) ? d : []).map(a => Number(a.dealership_id)))))
      .catch(() => setError('Failed to load assignments'))
      .finally(() => setLoading(false));
  };

  const toggle = (id) => {
    const n = Number(id);
    const next = new Set(assigned);
    next.has(n) ? next.delete(n) : next.add(n);
    setAssigned(next);
  };

  const save = async () => {
    const h = authHeader();
    if (!h || !selected) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/photographer/photographers/${selected.id}/assignments`, {
        method: 'PUT',
        headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealership_ids: [...assigned] }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setError(e.detail || 'Save failed');
        return;
      }
      setMessage(`Saved — ${assigned.size} dealership(s) assigned to ${selected.name || selected.email}`);
      setTimeout(() => setMessage(''), 3000);
      loadPhotographers();           // refresh assignment counts
    } catch (e) { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const shownPhotogs = photographers.filter(p => {
    const q = photogSearch.trim().toLowerCase();
    return !q || `${p.name || ''} ${p.email || ''}`.toLowerCase().includes(q);
  });

  const filteredDealers = dealers.filter(d => {
    const q = dealerSearch.trim().toLowerCase();
    if (!q) return true;
    return `${d.dealership_name || d.name} ${d.dealer_code || ''} ${d.city || ''} ${d.state || ''}`.toLowerCase().includes(q);
  });

  return (
    <div style={{ padding: 20, maxWidth: 1150, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Photographer Assignments</h2>
      </div>
      <p style={{ color: '#6b7280', marginTop: 0 }}>Pick a photographer, then check the dealerships they cover. Save replaces their full assignment set.</p>
      {error && <div style={{ color: '#dc2626', marginBottom: 8 }}>{error} <button onClick={() => setError('')} style={linkBtn}>dismiss</button></div>}
      {message && <div style={{ color: '#059669', marginBottom: 8 }}>{message}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 18 }}>
        {/* Photographer list */}
        <div style={panel}>
          <div style={panelHead}>Photographers ({shownPhotogs.length}{shownPhotogs.length !== photographers.length ? ` of ${photographers.length}` : ''})</div>
          <input placeholder="Search name / email…" value={photogSearch} onChange={e => setPhotogSearch(e.target.value)}
            style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box', margin: '8px 10px', width: 'calc(100% - 20px)' }} />
          <div style={{ maxHeight: '64vh', overflowY: 'auto' }}>
            {shownPhotogs.map(p => (
              <div key={p.id} onClick={() => selectPhotog(p)}
                style={{ ...row, background: selected && selected.id === p.id ? '#eef6ff' : '#fff' }}>
                <div style={{ fontWeight: 600 }}>{p.name || p.email}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{p.email}{!p.is_active && ' · inactive'}</div>
                <div style={{ fontSize: 12, color: '#0078D4' }}>{p.assignment_count || 0} dealer(s)</div>
              </div>
            ))}
            {!shownPhotogs.length && <div style={{ padding: 12, color: '#6b7280' }}>No photographers match.</div>}
          </div>
        </div>

        {/* Assignment editor */}
        <div style={panel}>
          {!selected ? (
            <div style={{ padding: 16, color: '#6b7280' }}>Select a photographer to edit their dealership coverage.</div>
          ) : (
            <>
              <div style={panelHead}>
                {selected.name || selected.email} — {assigned.size} selected
                <button onClick={save} disabled={saving} style={{ ...primaryBtn, float: 'right' }}>{saving ? 'Saving…' : 'Save assignments'}</button>
              </div>
              <input placeholder="Filter dealers…" value={dealerSearch} onChange={e => setDealerSearch(e.target.value)}
                style={{ ...inp, margin: '10px 12px', width: 'calc(100% - 24px)' }} />
              {loading ? <div style={{ padding: 12 }}>Loading assignments…</div> : (
                <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '0 12px 12px' }}>
                  {filteredDealers.map(d => (
                    <label key={d.id} style={checkRow}>
                      <input type="checkbox" checked={assigned.has(Number(d.id))} onChange={() => toggle(d.id)} />
                      <span>{d.dealership_name || d.name}
                        <span style={{ color: '#94a3b8', fontSize: 12 }}>  {d.dealer_code || ''}{d.city ? ` · ${d.city}, ${d.state || ''}` : ''}</span>
                      </span>
                    </label>
                  ))}
                  {!filteredDealers.length && <div style={{ color: '#6b7280', padding: 8 }}>No dealers match.</div>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const panel = { border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', overflow: 'hidden' };
const panelHead = { padding: '10px 12px', borderBottom: '1px solid #eef2f7', fontWeight: 600, fontSize: 14 };
const row = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' };
const checkRow = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', fontSize: 14, borderBottom: '1px solid #f8fafc', cursor: 'pointer' };
const linkBtn = { background: 'none', border: 'none', color: '#0078D4', cursor: 'pointer', padding: 0 };
const primaryBtn = { padding: '6px 14px', background: '#0078D4', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };
const inp = { padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' };
